/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Step';

const StepManager: ComponentManagerFn<'Step'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Step'>(platform, 'Step'),
    type: 'Step',
    name: 'Step',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'RowPanel', 'Box'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        required: true,
        helpText:
          'The color of the component. It supports those theme colors that make sense for this component.',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
        helpText: 'Icon to display on the step',
      },
    },
    category: 'Data',
    getDisplayText: (item) => '',
    icon: { iconName: 'server' },
    description: `**Overview**<br/>
The Step component is a structural building block designed specifically for constructing multi-step flows, such as Wizards, Timelines, or Progress Trackers.
It acts as a semantic container representing a single stage in a process. While visually similar to a layout box, it carries specific meaning within a workflow context—allowing you to define the appearance (Icon, Color) and behavior (Click Actions) of that specific stage.

**1. Content & Layout**<br/>
The Step component is a container. While it defines the stage, you must define what content appears inside that stage.
* **Allowed Children:** Row Panel / Box. Use these standard layout components to build the actual UI of the step—labels, descriptions, status indicators, or form summaries.
* **Note:** The Step itself does not enforce a specific layout; it gives you a canvas to design the visual representation of that stage.

**2. Interactivity**<br/>
Steps are often interactive navigation elements. Users might click a previous step to go back or click a future step to preview it.
* **TriggerOnClick:** You can add this child component to make the entire step clickable.
* **Common Workflow:** Bind this trigger to a logic flow that validates the current step and then navigates the user to the target step.

**4. Usage Context**<br/>
The Step component is rarely used in isolation. It is almost always a child of a larger parent container, such as a Stepper or Wizard component, which manages the overall flow and transitions between steps.`,
  };
};

export default StepManager;
